import {
  useState,
  useMemo,
  useRef,
  useEffect,
  type KeyboardEvent,
} from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Button } from './button';
import {
  PREFERRED_COUNTRIES,
  ALL_COUNTRIES,
  getCountryByCode,
} from '../../constants/countries';

interface CountrySelectorProps {
  id?: string;
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function CountrySelector({
  id,
  value,
  onValueChange,
  placeholder = 'Select country...',
  className,
  disabled = false,
}: CountrySelectorProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const optionsRef = useRef<Array<HTMLDivElement | null>>([]);
  const [focusButtonTick, setFocusButtonTick] = useState(0);

  const selectedCountry = value ? getCountryByCode(value) : undefined;

  useEffect(() => {
    if (!open) return;

    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
        setHighlightedIndex(0);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const filteredPreferred = useMemo(() => {
    if (!search) return PREFERRED_COUNTRIES;
    const searchLower = search.toLowerCase();
    return PREFERRED_COUNTRIES.filter(
      (country) =>
        country.label.toLowerCase().includes(searchLower) ||
        country.value.toLowerCase().includes(searchLower)
    );
  }, [search]);

  const filteredOthers = useMemo(() => {
    if (!search) {
      const preferredCodes = new Set(PREFERRED_COUNTRIES.map((c) => c.value));
      return ALL_COUNTRIES.filter((c) => !preferredCodes.has(c.value));
    }

    const searchLower = search.toLowerCase();
    const preferredCodes = new Set(PREFERRED_COUNTRIES.map((c) => c.value));

    return ALL_COUNTRIES.filter(
      (country) =>
        !preferredCodes.has(country.value) &&
        (country.label.toLowerCase().includes(searchLower) ||
          country.value.toLowerCase().includes(searchLower))
    );
  }, [search]);

  const showClearOption = !!value && !search;
  const clearOption = showClearOption
    ? { value: '', label: 'Clear selection', isClear: true as const }
    : null;

  const preferredStart = clearOption ? 1 : 0;
  const othersStart = preferredStart + filteredPreferred.length;

  const visibleCountries = [
    ...(clearOption ? [clearOption] : []),
    ...filteredPreferred,
    ...filteredOthers,
  ];

  useEffect(() => {
    const node = optionsRef.current[highlightedIndex];
    if (node) {
      node.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  useEffect(() => {
    if (focusButtonTick > 0) {
      buttonRef.current?.focus();
    }
  }, [focusButtonTick]);

  const closeDropdown = () => {
    setOpen(false);
    setSearch('');
    setHighlightedIndex(0);
    setFocusButtonTick((t) => t + 1);
  };

  const handleSelect = (countryValue: string) => {
    onValueChange(countryValue);
    closeDropdown();
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setHighlightedIndex((prev) =>
          visibleCountries.length === 0
            ? -1
            : (prev + 1) % visibleCountries.length
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setHighlightedIndex((prev) =>
          visibleCountries.length === 0
            ? -1
            : (prev - 1 + visibleCountries.length) % visibleCountries.length
        );
        break;
      case 'Enter': {
        event.preventDefault();
        const highlighted = visibleCountries[highlightedIndex];
        if (highlighted) handleSelect(highlighted.value);
        break;
      }
      case 'Escape':
        event.preventDefault();
        closeDropdown();
        break;
    }
  };

  const renderOption = (
    country: {
      value: string;
      label: string;
      flag?: string;
      isClear?: boolean;
    },
    flatIndex: number
  ) => (
    <div
      key={country.isClear ? '__clear__' : country.value}
      ref={(node) => {
        optionsRef.current[flatIndex] = node;
      }}
      role="option"
      aria-selected={!country.isClear && value === country.value}
      onMouseEnter={() => setHighlightedIndex(flatIndex)}
      className={cn(
        'relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-hidden transition-colors hover:bg-accent hover:text-accent-foreground',
        !country.isClear &&
          value === country.value &&
          'bg-accent text-accent-foreground',
        highlightedIndex === flatIndex && 'bg-accent text-accent-foreground',
        country.isClear && 'text-muted-foreground'
      )}
      onClick={() => handleSelect(country.value)}
    >
      {country.isClear ? (
        <>
          <X className="mr-2 h-4 w-4" />
          {country.label}
        </>
      ) : (
        <>
          <Check
            className={cn(
              'mr-2 h-4 w-4',
              value === country.value ? 'opacity-100' : 'opacity-0'
            )}
          />
          <span className="mr-2">{country.flag}</span>
          {country.label}
          <span className="ml-auto text-xs text-muted-foreground">
            {country.value}
          </span>
        </>
      )}
    </div>
  );

  return (
    <div className="relative">
      <Button
        ref={buttonRef}
        id={id}
        type="button"
        variant="outline"
        role="combobox"
        aria-expanded={open}
        className={cn(
          'w-full justify-between',
          !selectedCountry && 'text-muted-foreground',
          className
        )}
        disabled={disabled}
        onClick={() => {
          if (open) setHighlightedIndex(0);
          setOpen(!open);
        }}
      >
        {selectedCountry ? (
          <span className="flex items-center gap-2">
            <span>{selectedCountry.flag}</span>
            <span>{selectedCountry.label}</span>
          </span>
        ) : (
          placeholder
        )}
        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>

      {open && (
        <div
          ref={dropdownRef}
          className="absolute z-50 mt-1 w-full rounded-md border bg-popover p-0 text-popover-foreground shadow-md outline-hidden"
        >
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              className="flex h-11 w-full bg-transparent py-3 text-sm outline-hidden placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 border-0 focus:outline-hidden focus:ring-0"
              placeholder="Search countries..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setHighlightedIndex(0);
              }}
              onKeyDown={handleSearchKeyDown}
              autoFocus
            />
          </div>

          <div role="listbox" className="max-h-[300px] overflow-auto p-1">
            {visibleCountries.length === 0 ? (
              <div className="relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-hidden">
                No country found.
              </div>
            ) : (
              <>
                {clearOption && renderOption(clearOption, 0)}

                {clearOption &&
                  (filteredPreferred.length > 0 ||
                    filteredOthers.length > 0) && (
                    <div className="my-1 h-px bg-muted" />
                  )}

                {filteredPreferred.length > 0 && (
                  <>
                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                      Frequently used
                    </div>
                    {filteredPreferred.map((country, idx) =>
                      renderOption(country, preferredStart + idx)
                    )}
                  </>
                )}

                {filteredPreferred.length > 0 && filteredOthers.length > 0 && (
                  <div className="my-1 h-px bg-muted" />
                )}

                {filteredOthers.length > 0 && (
                  <>
                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                      All countries
                    </div>
                    {filteredOthers.map((country, idx) =>
                      renderOption(country, othersStart + idx)
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
